import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('mtss_token'));
  const [loading, setLoading] = useState(true);
  const [selectedSchoolId, setSelectedSchoolId] = useState(null); // For super_admin school switching
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      setMustChangePassword(response.data.must_change_password || false);
      setTermsAccepted(response.data.terms_accepted || false);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Refresh user data (e.g., after accepting terms)
  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API_URL}/auth/me`);
        setUser(response.data);
        setMustChangePassword(response.data.must_change_password || false);
        setTermsAccepted(response.data.terms_accepted || false);
        return response.data;
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData, must_change_password } = response.data;
    
    localStorage.setItem('mtss_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setSelectedSchoolId(null); // Reset school selection on login
    setMustChangePassword(must_change_password || false);
    setTermsAccepted(userData.terms_accepted || false);
    
    return { user: userData, mustChangePassword: must_change_password, termsAccepted: userData.terms_accepted };
  };

  const register = async (email, password, name, schoolId = null) => {
    const response = await axios.post(`${API_URL}/auth/register`, { 
      email, 
      password, 
      name,
      school_id: schoolId
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('mtss_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setMustChangePassword(false);
    
    return userData;
  };

  const completePasswordChange = () => {
    setMustChangePassword(false);
    if (user) {
      setUser({ ...user, must_change_password: false });
    }
  };

  const logout = () => {
    // Clear all authentication state
    localStorage.removeItem('mtss_token');
    delete axios.defaults.headers.common['Authorization'];
    
    // Clear any other potentially cached localStorage items
    // (preserves non-auth items like user preferences if they existed)
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('mtss_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear sessionStorage entirely on logout
    sessionStorage.clear();
    
    // Reset all state
    setToken(null);
    setUser(null);
    setSelectedSchoolId(null);
    setMustChangePassword(false);
  };

  // Role-based permission helpers - Updated for new role structure
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin;
  const isSpecialist = user?.role === 'specialist';
  const isViewer = user?.role === 'viewer';
  const canEdit = isSuperAdmin || isAdmin || isSpecialist;
  const canManageUsers = isSuperAdmin || isAdmin;
  const canAccessSettings = isSuperAdmin || isAdmin;
  const canManageSchools = isSuperAdmin;

  // Get the effective school ID (for API calls)
  const effectiveSchoolId = isSuperAdmin ? selectedSchoolId : user?.school_id;

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    // Password change
    mustChangePassword,
    completePasswordChange,
    // Terms acceptance
    termsAccepted,
    setTermsAccepted,
    // Role helpers
    isSuperAdmin,
    isAdmin,
    isSpecialist,
    isViewer,
    canEdit,
    canManageUsers,
    canAccessSettings,
    canManageSchools,
    // School helpers
    selectedSchoolId,
    setSelectedSchoolId,
    effectiveSchoolId,
    userSchoolId: user?.school_id,
    userSchoolName: user?.school_name
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
